import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';

interface CreditCosts {
  photoSwap: number;
  groupPhotoPerFace: number;
  videoSwap: number;
  watermarkRemoval: number;
  premiumTemplate: number;
  enhanceImage: number;
  restoreImage: number;
}

interface CreditRewards {
  rewardedAd: number; // Credits earned per ad watched (used for both legacy and new workflow)
}

interface FreeUserConfig {
  dailyFreePhotoSwapQuota: number; // Daily free photo swap quota per device (with ads)
}

interface ProductCredits {
  credits_100: number;
  credits_1200: number;
  credits_20000: number;
}

interface ProductDiscounts {
  credits_100: number;
  credits_1200: number;
  credits_20000: number;
}

interface ProductPrices {
  credits_100: number;
  credits_1200: number;
  credits_20000: number;
}

interface ProductTonPrices {
  credits_100: number;
  credits_1200: number;
  credits_20000: number;
}

interface CreditConfig {
  id?: string;
  costs: CreditCosts;
  rewards: CreditRewards;
  freeUser?: FreeUserConfig;
  productCredits?: ProductCredits;
  productDiscounts?: ProductDiscounts;
  productPrices?: ProductPrices;
  productTonPrices?: ProductTonPrices;
  isActive?: boolean;
  version?: number;
  lastUpdatedBy?: {
    adminId: string;
    adminName: string;
    adminEmail: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

const CreditConfiguration: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [config, setConfig] = useState<CreditConfig | null>(null);
  const [formData, setFormData] = useState<CreditConfig>({
    costs: {
      photoSwap: 5,
      groupPhotoPerFace: 5,
      videoSwap: 20,
      watermarkRemoval: 5,
      premiumTemplate: 2,
      enhanceImage: 5,
      restoreImage: 5,
    },
    rewards: {
      rewardedAd: 5,
    },
    freeUser: {
      dailyFreePhotoSwapQuota: 5,
    },
    productCredits: {
      credits_100: 100,
      credits_1200: 1200,
      credits_20000: 20000,
    },
    productDiscounts: {
      credits_100: 0,
      credits_1200: 0,
      credits_20000: 0,
    },
    productPrices: {
      credits_100: 2.99,
      credits_1200: 14.99,
      credits_20000: 99.99,
    },
    productTonPrices: {
      credits_100: 5,
      credits_1200: 20,
      credits_20000: 60,
    },
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getCreditConfig();

      if (response.success && response.data) {
        setConfig(response.data);
        setFormData({
          costs: response.data.costs,
          rewards: {
            rewardedAd: response.data.rewards?.rewardedAd ?? 5,
          },
          freeUser: {
            dailyFreePhotoSwapQuota: response.data.freeUser?.dailyFreePhotoSwapQuota ?? 5,
          },
          productCredits: response.data.productCredits || {
            credits_100: 100,
            credits_1200: 1200,
            credits_20000: 20000,
          },
          productDiscounts: response.data.productDiscounts || {
            credits_100: 0,
            credits_1200: 0,
            credits_20000: 0,
          },
          productPrices: response.data.productPrices || {
            credits_100: 2.99,
            credits_1200: 14.99,
            credits_20000: 99.99,
          },
          productTonPrices: response.data.productTonPrices || {
            credits_100: 5,
            credits_1200: 20,
            credits_20000: 60,
          },
        });
      } else {
        setError(response.error || 'Failed to fetch credit configuration');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch credit configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleCostChange = (field: keyof CreditCosts, value: string) => {
    const numValue = parseInt(value) || 0;
    setFormData((prev) => ({
      ...prev,
      costs: {
        ...prev.costs,
        [field]: numValue < 0 ? 0 : numValue,
      },
    }));
  };

  const handleRewardChange = (field: keyof CreditRewards, value: string) => {
    const numValue = parseInt(value) || 0;
    setFormData((prev) => ({
      ...prev,
      rewards: {
        ...prev.rewards,
        [field]: numValue < 0 ? 0 : numValue,
      },
    }));
  };

  const handleFreeUserChange = (field: keyof FreeUserConfig, value: string) => {
    const numValue = parseInt(value) || 0;
    setFormData((prev) => ({
      ...prev,
      freeUser: {
        ...(prev.freeUser || { dailyFreePhotoSwapQuota: 5 }),
        [field]: numValue < 0 ? 0 : numValue,
      },
    }));
  };

  const handleProductCreditChange = (field: keyof ProductCredits, value: string) => {
    const numValue = parseInt(value) || 0;
    setFormData((prev) => ({
      ...prev,
      productCredits: {
        ...(prev.productCredits || {
          credits_100: 100,
          credits_1200: 1200,
          credits_20000: 20000,
        }),
        [field]: numValue < 0 ? 0 : numValue,
      },
    }));
  };

  const handleProductDiscountChange = (field: keyof ProductDiscounts, value: string) => {
    const numValue = parseFloat(value) || 0;
    setFormData((prev) => ({
      ...prev,
      productDiscounts: {
        ...(prev.productDiscounts || {
          credits_100: 0,
          credits_1200: 0,
          credits_20000: 0,
        }),
        [field]: numValue < 0 ? 0 : numValue > 100 ? 100 : numValue,
      },
    }));
  };

  const handleProductPriceChange = (field: keyof ProductPrices, value: string) => {
    const numValue = parseFloat(value) || 0;
    setFormData((prev) => ({
      ...prev,
      productPrices: {
        ...(prev.productPrices || {
          credits_100: 2.99,
          credits_1200: 14.99,
          credits_20000: 99.99,
        }),
        [field]: numValue < 0 ? 0 : numValue,
      },
    }));
  };

  const handleProductTonPriceChange = (field: keyof ProductTonPrices, value: string) => {
    const numValue = parseFloat(value) || 0;
    setFormData((prev) => ({
      ...prev,
      productTonPrices: {
        ...(prev.productTonPrices || {
          credits_100: 5,
          credits_1200: 20,
          credits_20000: 60,
        }),
        [field]: numValue < 0 ? 0 : numValue,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const response = await apiService.updateCreditConfig({
        costs: formData.costs,
        rewards: formData.rewards,
        freeUser: formData.freeUser,
        productCredits: formData.productCredits,
        productDiscounts: formData.productDiscounts,
        productPrices: formData.productPrices,
        productTonPrices: formData.productTonPrices,
      });

      if (response.success) {
        toast.success('Credit configuration updated successfully');
        await fetchConfig(); // Refresh to get updated version
      } else {
        setError(response.error || 'Failed to update credit configuration');
        toast.error(response.error || 'Failed to update credit configuration');
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to update credit configuration';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Credit Configuration</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchConfig}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            Save Changes
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {config && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Typography variant="body2" color="text.secondary">
            Version: {config.version} | Last updated: {config.updatedAt ? new Date(config.updatedAt).toLocaleString() : 'N/A'}
            {config.lastUpdatedBy && ` by ${config.lastUpdatedBy.adminEmail}`}
          </Typography>
        </Paper>
      )}

      <Grid container spacing={3}>
        {/* Credit Costs */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CreditCardIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Credit Costs</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Photo Swap"
                    type="number"
                    value={formData.costs.photoSwap}
                    onChange={(e) => handleCostChange('photoSwap', e.target.value)}
                    helperText="Credits required for single photo face swap"
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Group Photo (Per Face)"
                    type="number"
                    value={formData.costs.groupPhotoPerFace}
                    onChange={(e) => handleCostChange('groupPhotoPerFace', e.target.value)}
                    helperText="Credits required per face in group photo swap"
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Video Swap"
                    type="number"
                    value={formData.costs.videoSwap}
                    onChange={(e) => handleCostChange('videoSwap', e.target.value)}
                    helperText="Credits required for video face swap"
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Watermark Removal"
                    type="number"
                    value={formData.costs.watermarkRemoval}
                    onChange={(e) => handleCostChange('watermarkRemoval', e.target.value)}
                    helperText="Credits required to remove watermark"
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Premium Template"
                    type="number"
                    value={formData.costs.premiumTemplate}
                    onChange={(e) => handleCostChange('premiumTemplate', e.target.value)}
                    helperText="Credits required for premium template usage"
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Enhance Image"
                    type="number"
                    value={formData.costs.enhanceImage}
                    onChange={(e) => handleCostChange('enhanceImage', e.target.value)}
                    helperText="Credits required for image enhancement"
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Restore Old Image"
                    type="number"
                    value={formData.costs.restoreImage}
                    onChange={(e) => handleCostChange('restoreImage', e.target.value)}
                    helperText="Credits required for old image restoration"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Credit Rewards */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CreditCardIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6">Credit Rewards</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Rewarded Ad / Credits Per Ad"
                    type="number"
                    value={formData.rewards.rewardedAd}
                    onChange={(e) => handleRewardChange('rewardedAd', e.target.value)}
                    helperText="Credits earned per ad watched (used for earning credits)"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Free User Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CreditCardIcon sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">Free User Settings</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Daily Free Photo Swap Quota"
                    type="number"
                    value={formData.freeUser?.dailyFreePhotoSwapQuota || 5}
                    onChange={(e) => handleFreeUserChange('dailyFreePhotoSwapQuota', e.target.value)}
                    helperText="Number of free photo swaps per device per day (with ads, no sign-in required)"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Product Credits */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CreditCardIcon sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="h6">Product Credits (In-App Purchases)</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure how many credits each product ID should grant when purchased, discount percentage for displaying original prices, product price in USD (for in-app purchases), and product price in TON cryptocurrency (for Telegram Mini App purchases)
              </Typography>

              <Grid container spacing={2}>
                {/* Product 10 */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    credits_100
                  </Typography>
                  <TextField
                    fullWidth
                    label="Credits"
                    type="number"
                    value={formData.productCredits?.credits_100 || 100}
                    onChange={(e) => handleProductCreditChange('credits_100', e.target.value)}
                    helperText="Credits granted"
                    inputProps={{ min: 0 }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Price (USD)"
                    type="number"
                    value={formData.productPrices?.credits_100 || 2.99}
                    onChange={(e) => handleProductPriceChange('credits_100', e.target.value)}
                    helperText="Product price in USD"
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Price (TON)"
                    type="number"
                    value={formData.productTonPrices?.credits_100 || 5}
                    onChange={(e) => handleProductTonPriceChange('credits_100', e.target.value)}
                    helperText="Product price in TON (for Telegram Mini App)"
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Discount %"
                    type="number"
                    value={formData.productDiscounts?.credits_100 || 0}
                    onChange={(e) => handleProductDiscountChange('credits_100', e.target.value)}
                    helperText="Discount percentage (0-100)"
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                </Grid>
                {/* Product 20 */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    credits_1200
                  </Typography>
                  <TextField
                    fullWidth
                    label="Credits"
                    type="number"
                    value={formData.productCredits?.credits_1200 || 1200}
                    onChange={(e) => handleProductCreditChange('credits_1200', e.target.value)}
                    helperText="Credits granted"
                    inputProps={{ min: 0 }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Price (USD)"
                    type="number"
                    value={formData.productPrices?.credits_1200 || 14.99}
                    onChange={(e) => handleProductPriceChange('credits_1200', e.target.value)}
                    helperText="Product price in USD"
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Price (TON)"
                    type="number"
                    value={formData.productTonPrices?.credits_1200 || 20}
                    onChange={(e) => handleProductTonPriceChange('credits_1200', e.target.value)}
                    helperText="Product price in TON (for Telegram Mini App)"
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Discount %"
                    type="number"
                    value={formData.productDiscounts?.credits_1200 || 0}
                    onChange={(e) => handleProductDiscountChange('credits_1200', e.target.value)}
                    helperText="Discount percentage (0-100)"
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                </Grid>
                {/* Product 30 */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    credits_20000
                  </Typography>
                  <TextField
                    fullWidth
                    label="Credits"
                    type="number"
                    value={formData.productCredits?.credits_20000 || 20000}
                    onChange={(e) => handleProductCreditChange('credits_20000', e.target.value)}
                    helperText="Credits granted"
                    inputProps={{ min: 0 }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Price (USD)"
                    type="number"
                    value={formData.productPrices?.credits_20000 || 99.99}
                    onChange={(e) => handleProductPriceChange('credits_20000', e.target.value)}
                    helperText="Product price in USD"
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Price (TON)"
                    type="number"
                    value={formData.productTonPrices?.credits_20000 || 60}
                    onChange={(e) => handleProductTonPriceChange('credits_20000', e.target.value)}
                    helperText="Product price in TON (for Telegram Mini App)"
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Discount %"
                    type="number"
                    value={formData.productDiscounts?.credits_20000 || 0}
                    onChange={(e) => handleProductDiscountChange('credits_20000', e.target.value)}
                    helperText="Discount percentage (0-100)"
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </Box>
    </Box>
  );
};

export default CreditConfiguration;

